import { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import { useApolloClient, DocumentNode, ApolloClient } from '@apollo/client';

import { CompleteBongQuery_venue } from './__queries__';
import COMPLETE_QUERY from './completeQuery';
import { useI18n, useLogger, useSelector } from '~Hooks';

export const ITEM_FRAGMENT = gql`
  fragment itemInBong on Item {
    id
    tags
    ageRestricted
    defaultOrderStatus
    courseType {
      id
      color
      label
      name
      order
    }
    manualPrice
    manualName
    defaultPrice
    volumeSuffix
    titleLang {
      id
      se
      en
      es
      no
    }
    vatRate
    descriptionLang {
      id
      se
      en
      es
      no
    }
    allergens
    upsalesSortOrder
    upsales {
      id
    }

    image {
      id
    }

    modificationGroups
    allergyTags
    costBasis
    disableMembershipDiscount
  }
`;

const FRAGMENTS = {
  ModificationGroup: gql`
    fragment modificationGroupInBong on ModificationGroup {
      id
      internalName
      externalName
      type
      required
      expandedByDefault
      preselectedVariations
      isGridViewEnabled
      variations {
        id
        name
        nameLang {
          id
          se
          en
          es
          no
        }
        price
        allergen
      }
    }
  `,

  Upsell: gql`
    fragment upsellsInBong on Upsell {
      id
      hideInPOS
      name {
        id
        se
        en
        es
        no
      }
      items {
        id
      }
    }
  `,

  Offer: gql`
    fragment offerInBong on Offer {
      id
      enabled
      nameLang {
        id
        se
        en
        es
        no
      }
      image {
        id
      }
      type
      discountType
      discountRate
      overrideItemPrices

      items(filter: { deleted: false }) {
        id
      }

      categories {
        id
        items(filter: { deleted: false }) {
          id
        }
      }

      stages {
        id
        discountRate
        discountType
        title {
          id
          se
          en
          es
          no
        }
        items(filter: { deleted: false }) {
          id
        }
      }
    }
  `,
};

const VENUE_FRAGMENT = gql`
  fragment venueInBong on Venue {
    id

    company {
      id
      defaultOrderStatus
      offers(context: [POS]) {
        ...offerInBong
      }

      layouts {
        id
        name {
          id
          se
          en
          no
          es
        }
        categories {
          id
          name {
            id
            se
            en
            no
            es
          }
          order
          color
          page {
            id
            name {
              id
              se
              en
              no
              es
            }
            rows
            columns
            buttons
          }
        }
      }

      pages {
        id
        name {
          id
          se
          en
          no
          es
        }
        rows
        columns
        buttons
      }
    }
  }

  ${FRAGMENTS.Offer}
`;

const VARIATIONS_QUERY = gql`
  query getVariationsForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        variations {
          id
          tags
          highlighted
          name
          nameLang {
            id
            se
            en
            es
            no
          }
          asItem {
            id
          }
          price
          image {
            id
          }
          allergen
          order
        }
      }
    }
  }
`;

const MODIFICATION_GROUPS_QUERY = gql`
  query getModificationGroupForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        modificationGroups {
          ...modificationGroupInBong
        }
      }
    }
  }

  ${FRAGMENTS.ModificationGroup}
`;

const UPSALES_QUERY = gql`
  query getUpsalesForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        upsells {
          ...upsellsInBong
        }
      }
    }
  }

  ${FRAGMENTS.Upsell}
`;

const ITEM_CONTENT_QUERY = gql`
  query GetAllItems($companyId: ID!, $first: Int!, $skip: Int!) {
    allItems(first: $first, skip: $skip, filter: { deleted: false, company: { id: $companyId } }) {
      ...itemInBong
    }
  }
  ${ITEM_FRAGMENT}
`;

const ITEMS_QUERY = gql`
  query getItemsForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      name
      company {
        id
        name

        items(filter: { deleted: false }) {
          id
        }
      }
    }
  }
`;

const VENUE_QUERY = gql`
  query GetVenueForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      ...venueInBong
    }
  }

  ${VENUE_FRAGMENT}
`;

const VENUE_CATEGORIES_QUERY = gql`
  query getVenueCategories($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      name

      company {
        id
        name
        categories {
          id
          order
          color
          ukVatRate

          nameLang {
            id
            se
            en
            es
            no
          }
          descriptionLang {
            id
            se
            en
            es
            no
          }

          items(filter: { deleted: false }) {
            id
          }

          image {
            id
          }
        }
      }
    }
  }
`;

const IMAGES_QUERY = gql`
  query getImagesForBong($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id

        images {
          id
          file {
            id
            url
          }
        }
      }
    }
  }
`;

const FLOOR_PLANS_QUERY = gql`
  query getFloorPlans($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      name
      floorPlans {
        id
        name

        tables {
          id
          code
          rotation
          posX
          posY
          deleted

          floorPlan {
            id
          }
        }
      }
    }
  }
`;

const useBongQuery = (venueId: string | null) => {
  const client = useApolloClient();
  const logger = useLogger('bong-query');

  const demoMode = useSelector((state) => state.demoMode);
  let companyId = useSelector((state) => state.company);
  if (demoMode) {
    companyId = window.DEMO_MODE_COMPANY_ID;
  }

  const [loading, setLoading] = useState<boolean>(true);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Loading...');
  const [data, setData] = useState<CompleteBongQuery_venue | null>(null);
  const { i18n } = useI18n();

  const queryAndRetry = (
    client: ApolloClient<any>,
    query: DocumentNode,
    variables: object | null,
    iteration = 0,
  ) => {
    //@ts-ignore
    const queryName = (query.definitions[0] as any)?.name?.value;
    function onFail() {
      return new Promise((resolve, reject) => {
        if (iteration >= 5) {
          logger.error(`Bong query: failed to fetch ${queryName} 5 times, no more retries.`);
          resolve(null);
          return;
        }

        setTimeout(() => {
          queryAndRetry(client, query, variables, iteration + 1)
            .then(resolve)
            .catch(reject);
        }, iteration * 250);
      });
    }

    return client
      .query({
        query,
        variables: variables || {},
      })
      .then((response) => {
        if (!response.data) {
          console.warn(response);
          logger.warn(`Bong query: failed to fetch ${queryName}, retrying...`, {
            errorResponse: response,
          });
          return onFail();
        }

        return response;
      })
      .catch(onFail);
  };

  const randomOffset = (max = 9) => Math.round(Math.random() * max);

  async function runQuery() {
    setProgress(5 + randomOffset());
    setProgressText(i18n('POS.Loading.Variations'));

    await queryAndRetry(client, VARIATIONS_QUERY, {
      venueId,
    });

    setProgress(15 + randomOffset());
    setProgressText(i18n('POS.Loading.ModificationGroups'));

    await queryAndRetry(client, MODIFICATION_GROUPS_QUERY, {
      venueId,
    });

    await queryAndRetry(client, FLOOR_PLANS_QUERY, {
      venueId,
    });

    setProgress(25 + randomOffset());
    setProgressText(i18n('POS.Loading.Upsales'));

    await queryAndRetry(client, UPSALES_QUERY, {
      venueId,
    });

    setProgress(35 + randomOffset());
    setProgressText(i18n('POS.Loading.Images'));

    await queryAndRetry(client, IMAGES_QUERY, {
      venueId,
    });

    setProgress(45 + randomOffset());
    setProgressText(i18n('POS.Loading.Venue'));

    await queryAndRetry(client, VENUE_QUERY, {
      venueId,
    });

    setProgressText(i18n('POS.Loading.Articles'));

    const INCREMENT = 2;

    // This makes us handle a theoretical maximum of ~2800 items
    for (let i = 0; i < 20; i++) {
      setProgress(55 + i * INCREMENT + 1 + randomOffset(INCREMENT));
      await queryAndRetry(client, ITEM_CONTENT_QUERY, {
        companyId,
        first: 151,
        skip: i * 150,
      });
    }

    setProgress(90 + randomOffset(3));
    setProgressText(i18n('POS.Loading.Metadata'));

    await queryAndRetry(client, ITEMS_QUERY, {
      venueId,
    });

    setProgress(94 + randomOffset(5));
    setProgressText(i18n('POS.Loading.Categories'));

    await queryAndRetry(client, VENUE_CATEGORIES_QUERY, {
      venueId,
    });

    /**
     * This is the complete query. It fetches the whole resource
     * object from the cache. Finishes immediately.
     */
    const response = client.watchQuery({
      query: COMPLETE_QUERY,
      variables: {
        id: venueId,
      },
      fetchPolicy: 'cache-only',
      returnPartialData: true,
    });

    const result = await response.result();

    // We're finished!
    setProgress(100);
    setLoading(false);
    setData(result.data.venue);
  }

  useEffect(() => {
    if (!companyId || !venueId) {
      return;
    }

    runQuery();
  }, [companyId, venueId]);

  return { loading, progress, progressText, data };
};

export default useBongQuery;
